import { create } from 'zustand';
import { logger } from '@/lib/logger';
import type { IBackend } from '@/services/backend/IBackend';
import { useTerminalStore } from './terminalStore';

/**
 * Store for tracking project activity for the sidebar indicator.
 * When a terminal receives output, we mark the project as having activity.
 * The activity indicator pulses green then fades out after ~3s of inactivity.
 *
 * Uses event-driven expiry (setTimeout per activity) instead of polling,
 * so components only re-render when activity state actually changes.
 */

interface ProjectActivityState {
  // Map of projectKey -> expiration timestamp
  activities: Map<string, number>;

  // Mark a project as having recent activity
  markActivity: (projectKey: string) => void;

  // Check if a project currently has activity (not expired)
  hasActivity: (projectKey: string) => boolean;

  // Clear activity for a specific project
  clearActivity: (projectKey: string) => void;
}

const ACTIVITY_DURATION_MS = 3 * 1000; // 3 seconds — responsive fade after terminal stops

// Track expiry timeouts so we can cancel/replace them
const expiryTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const useProjectActivityStore = create<ProjectActivityState>((set, get) => ({
  activities: new Map(),

  markActivity: (projectId: string) => {
    const expiresAt = Date.now() + ACTIVITY_DURATION_MS;

    // Cancel any existing expiry timer for this project
    const existing = expiryTimers.get(projectId);
    if (existing) clearTimeout(existing);

    // Schedule removal when activity expires — triggers a single re-render
    const timer = setTimeout(() => {
      expiryTimers.delete(projectId);
      set((state) => {
        const newActivities = new Map(state.activities);
        newActivities.delete(projectId);
        return { activities: newActivities };
      });
    }, ACTIVITY_DURATION_MS);
    expiryTimers.set(projectId, timer);

    set((state) => {
      const newActivities = new Map(state.activities);
      newActivities.set(projectId, expiresAt);
      return { activities: newActivities };
    });
  },

  hasActivity: (projectId: string) => {
    const expiresAt = get().activities.get(projectId);
    if (!expiresAt) return false;
    return Date.now() < expiresAt;
  },

  clearActivity: (projectId: string) => {
    const timer = expiryTimers.get(projectId);
    if (timer) {
      clearTimeout(timer);
      expiryTimers.delete(projectId);
    }
    set((state) => {
      const newActivities = new Map(state.activities);
      newActivities.delete(projectId);
      return { activities: newActivities };
    });
  },
}));

// No-op stubs — kept for API compatibility but cleanup is now event-driven
export function startActivityCleanup() {}
export function stopActivityCleanup() {}

// --- Terminal activity tracking ---
//
// The pty-daemon already emits a FILTERED per-session activity signal for EVERY
// session (session.rs: strips ANSI cursor/status/spinner noise, fires only when
// ≥5 chars of meaningful visible text change), and does so for BACKGROUND /
// detached sessions — exactly the "which OTHER project is an agent busy in" case.
// We just have to listen to it (`onActivity`). For the ATTACHED / foreground
// terminal the daemon streams raw Data instead of the activity signal, so we
// apply the same visible-text filter to `onData` so the project you ARE looking
// at also pulses — without the constant TUI-repaint noise.

let unsubTerminalTracking: (() => void) | null = null;

/** Strip ANSI escape/OSC sequences + control chars, leaving printable text. */
function visibleText(s: string): string {
  return s
    .replace(/\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)/g, '') // OSC … BEL / ST
    .replace(/\x1b\[[0-9;?=]*[ -/]*[@-~]/g, '')         // CSI …
    .replace(/\x1b[@-_]/g, '')                           // other single-char ESC
    .replace(/[\x00-\x1f\x7f]/g, '')                     // control chars
    .trim();
}

export function startTerminalActivityTracking(backend: IBackend): void {
  if (unsubTerminalTracking) return; // already tracking

  const markById = (id: number) => {
    const session = useTerminalStore.getState().sessions.get(id);
    if (session) useProjectActivityStore.getState().markActivity(session.projectPath);
  };

  // Background/detached agent sessions: daemon-filtered activity for ALL sessions.
  const unsubActivity = backend.terminal.onActivity((id: number) => markById(id));

  // Foreground/attached session: raw data — apply the same "meaningful visible
  // text" filter + a light throttle so it pulses without the idle-repaint flood.
  const lastMark = new Map<number, number>();
  const unsubData = backend.terminal.onData((id: number, data: string) => {
    if (visibleText(data).length < 5) return;
    const now = Date.now();
    if (now - (lastMark.get(id) ?? 0) < 800) return;
    lastMark.set(id, now);
    markById(id);
  });

  unsubTerminalTracking = () => {
    unsubActivity();
    unsubData();
    lastMark.clear();
  };
  logger.debug('ProjectActivityStore', 'Terminal activity tracking started (onActivity + filtered onData)');
}

export function stopTerminalActivityTracking(): void {
  if (unsubTerminalTracking) {
    unsubTerminalTracking();
    unsubTerminalTracking = null;
  }
  logger.debug('ProjectActivityStore', 'Terminal activity tracking stopped');
}
